import styled from 'styled-components'
import { useFormikContext } from 'formik'
import { Grid } from '@farewill/ui'
import { COLOR, FONT, GTR } from '@farewill/ui/tokens'

import InputFloating from 'components/form/input-floating'
import ErrorSmall from 'components/form/error-small'
import { GIFT_SCHEMA } from 'lib/probate/estate-questionnaire-schema'

import { FieldRow, FieldRowItem } from '../components/field-row'
import RecordsSection, {
  RecordsSubSectionProps,
  RenderRecordFields,
} from '../estate/records-section'
import getValueTotal from '../estate/value-total'

const StyledFieldset = styled.fieldset`
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;
`

// matches the other questions' labels
const StyledLegend = styled.legend`
  padding: 0;
  margin-bottom: ${GTR.XS};
  color: ${COLOR.GREY.DARK};
  font-weight: ${FONT.WEIGHT.MEDIUM};
`

// in the order they're asked for, as each needs the one before it
const GIFT_DATE_PARTS = ['giftYear', 'giftMonth', 'giftDay']

const isWholeGiftDate = (values: Record<string, unknown>) => {
  try {
    GIFT_SCHEMA.validateSyncAt('gift', values)
    return true
  } catch {
    return false
  }
}

type HandleSave = Parameters<RenderRecordFields>[0]['handleSave']

// An approximate date: just a year, a month and year, or a full date. The
// year comes first and each later part only opens once the one before it
// is in, so any of them can be typed by tabbing straight through
const GiftDate = ({ handleSave }: { handleSave: HandleSave }) => {
  const { values, setFieldValue } = useFormikContext<Record<string, unknown>>()
  const hasYear = !!values.giftYear
  const hasMonth = hasYear && !!values.giftMonth

  const saveDate = ({ name }: { name: string }) => {
    const date = { ...values }

    // a month or day can't stand without the parts before it, so clearing
    // a part clears the ones after it too
    if (!date[name]) {
      GIFT_DATE_PARTS.slice(GIFT_DATE_PARTS.indexOf(name) + 1).forEach(
        (part) => {
          date[part] = ''
          setFieldValue(part, '')
        }
      )
    }

    if (!isWholeGiftDate(date)) return
    GIFT_DATE_PARTS.forEach((part) =>
      handleSave({ name: part, value: (date[part] as string | null) ?? null })
    )
  }

  return (
    <StyledFieldset>
      <StyledLegend>Approximate date of gift</StyledLegend>
      <FieldRow>
        <FieldRowItem>
          <InputFloating name="giftYear" label="Year" handleSave={saveDate} />
        </FieldRowItem>
        <FieldRowItem>
          <InputFloating
            name="giftMonth"
            label="Month"
            disabled={!hasYear}
            handleSave={saveDate}
          />
        </FieldRowItem>
        <FieldRowItem>
          <InputFloating
            name="giftDay"
            label="Day"
            disabled={!hasMonth}
            handleSave={saveDate}
          />
        </FieldRowItem>
      </FieldRow>
      <ErrorSmall name="gift" />
    </StyledFieldset>
  )
}

const Gifts = ({
  collection,
  sectionActive,
  saveProbateEstateField,
}: RecordsSubSectionProps) => (
  <RecordsSection
    heading="4.1 Gifts in the last 7 years"
    cardType="Gift"
    addLabel="Add gift"
    toggleName="giftsSectionActive"
    sectionActive={sectionActive}
    saveProbateEstateField={saveProbateEstateField}
    collection={collection}
    totals={{ total: getValueTotal(collection.items) }}
    schema={GIFT_SCHEMA}
    renderFields={({ handleSave }) => (
      <>
        <Grid.Item span={12}>
          <InputFloating
            name="description"
            label="Gift description"
            handleSave={handleSave}
          />
        </Grid.Item>
        <Grid.Item span={12}>
          <GiftDate handleSave={handleSave} />
        </Grid.Item>
        <Grid.Item span={12}>
          <InputFloating
            name="value"
            label="Value of gift (£)"
            isCurrency
            handleSave={handleSave}
          />
        </Grid.Item>
      </>
    )}
  />
)

export default Gifts
